import { Box, Theme } from '@mui/material'
import { GridColumns } from '@mui/x-data-grid-pro'
import { TFunction } from 'react-i18next'
import { Chip } from 'src/components/common/Chip'
import { DynamicTextTypography } from 'src/components/DynamicTextTypography'
import { RowMaintenanceTypeImageBox } from 'src/components/locality-dashboard/LocalityMaintenanceGrid'
import { DeviationStatus, MaintenanceType } from 'src/enums'
import { IScheduledServiceDeviation } from 'src/generated-types/locality-frequency'
import { MUIThemeColors } from 'src/mui-theme'
import { Padding } from 'src/styles/paddingComponent'
import { AppRoutesNavigation } from '../../../app-routes'
import { IDeviation } from '../../../generated-types/deviation'
import { IDeviationPriority } from '../../../generated-types/deviation-priority'
import { getFormattedDate, getThemeColor } from '../../../utils'

export const minimal_data_deviation_static_column = (
  t: TFunction<('common' | 'deviation')[], undefined>,
  theme: Theme,
  navigateTo?: (path: string) => void,
  customerId?: EntityId,
  localityId?: EntityId,
  pop?: () => void,
  disabled?: boolean,
): GridColumns => [
  {
    field: 'title',
    headerName: t('title', { ns: 'common' }),
    flex: 1.5,
    renderCell: ({ row }) => {
      const deviation = row as IDeviation
      const inApproval = deviation.deviationStatus === DeviationStatus.InApproval
      const isClosedOrPending = deviation.status || inApproval
      const statusLabel = inApproval
        ? t('pending', { ns: 'deviation' })
        : deviation.status
        ? t('closed', { ns: 'common' })
        : null
      return (
        <Box
          display={'flex'}
          flexDirection={'row'}
          gap={'8px'}
          alignContent={'center'}
          justifyContent={'center'}
          alignItems={'center'}
        >
          <DynamicTextTypography
            text={deviation.title}
            maxLength={30}
            color={
              isClosedOrPending
                ? getThemeColor(theme, MUIThemeColors.secondaryDark)
                : getThemeColor(theme, MUIThemeColors.black)
            }
            fontWeight={500}
          />
          {statusLabel != null && (
            <Chip
              label={statusLabel}
              variant="filled"
              sx={{
                backgroundColor: inApproval
                  ? getThemeColor(theme, MUIThemeColors.warningMain)
                  : getThemeColor(theme, MUIThemeColors.primaryLight),
                color: `${getThemeColor(theme, MUIThemeColors.white)} !important`,
                height: '16px',
                borderRadius: '2px',
                fontSize: '11px',
                paddingTop: '1px',
              }}
            />
          )}
        </Box>
      )
    },
  },
  {
    field: 'responsiblePersonName',
    headerName: t('responsible', { ns: 'deviation' }),
    minWidth: 200,
    flex: 1,
    renderCell: ({ row }) => {
      const isClosedOrPending = row.status || (row as IDeviation).deviationStatus === DeviationStatus.InApproval
      return (
        <DynamicTextTypography
          text={row.responsiblePersonName || '-'}
          maxLength={20}
          color={
            isClosedOrPending
              ? getThemeColor(theme, MUIThemeColors.secondaryDark)
              : getThemeColor(theme, MUIThemeColors.black)
          }
          fontWeight={400}
        />
      )
    },
  },
  {
    field: 'priority',
    headerName: t('priority', { ns: 'common' }),
    flex: 1,
    minWidth: 100,
    renderCell: value =>
      value.value == IDeviationPriority.Critical ? (
        <Chip
          label={t('critical', { ns: 'common' })}
          variant="filled"
          sx={{
            backgroundColor: getThemeColor(theme, MUIThemeColors.errorMain),
            color: 'white !important',
            height: '24px',
            borderRadius: '4px',
          }}
        />
      ) : (
        <Chip
          label={t('normal', { ns: 'common' })}
          variant="outlined"
          sx={{ borderColor: getThemeColor(theme, MUIThemeColors.secondaryLight), height: '24px', borderRadius: '4px' }}
        />
      ),
  },
  {
    field: 'origin',
    headerName: t('origin', { ns: 'common' }),
    minWidth: 170,
    flex: 1,
    renderCell: ({ row }) => {
      const deviation = row as IDeviation
      const isClosedOrPending = deviation.status || deviation.deviationStatus === DeviationStatus.InApproval
      const scheduledServiceDeviations = deviation.scheduledServiceDeviations?.reduce(
        (deviations: IScheduledServiceDeviation[], dev) => {
          const existingItemIndex = deviations.findIndex(item => item.groupId === dev.groupId)
          if (existingItemIndex === -1) {
            deviations.push({ ...dev })
          } else {
            deviations[existingItemIndex].title +=
              deviations[existingItemIndex].mooringServiceId != dev.mooringServiceId ? ` | ${dev.title}` : ''
          }

          return deviations
        },
        [],
      )

      if (deviation?.maintenance?.name) {
        return (
          <>
            <Padding.pr4>{RowMaintenanceTypeImageBox({ type: MaintenanceType.DailyMaintenance })}</Padding.pr4>
            <DynamicTextTypography
              text={deviation.maintenance.name}
              onClick={e => {
                if (disabled) return
                e.stopPropagation()
                e.preventDefault()
                if (
                  navigateTo &&
                  (deviation?.locality?.customerId || customerId) &&
                  (deviation?.locality?.id || localityId)
                ) {
                  navigateTo(
                    `${AppRoutesNavigation.Localities.localityServices(
                      (deviation?.locality?.customerId ?? customerId) as EntityId,
                      (deviation?.locality?.id ?? localityId) as EntityId,
                    )}#maintenance-${deviation.maintenance?.id}`,
                  )
                }
                pop && pop()
              }}
              maxLength={12}
              color={
                isClosedOrPending
                  ? getThemeColor(theme, MUIThemeColors.secondaryDark)
                  : getThemeColor(theme, MUIThemeColors.black)
              }
              fontWeight={400}
            />
          </>
        )
      }

      return scheduledServiceDeviations && scheduledServiceDeviations?.length != 0 ? (
        <>
          <Padding.pr4>{RowMaintenanceTypeImageBox({ type: MaintenanceType.Service })}</Padding.pr4>
          {scheduledServiceDeviations.map(localityFrequencyDeviation => (
            <DynamicTextTypography
              key={localityFrequencyDeviation.id}
              onClick={e => {
                if (disabled) return
                e.stopPropagation()
                e.preventDefault()
                if (
                  navigateTo &&
                  (localityFrequencyDeviation.locality?.customerId || deviation?.locality?.customerId || customerId) &&
                  (localityFrequencyDeviation.locality?.id || deviation?.locality?.id || localityId)
                ) {
                  if (localityFrequencyDeviation?.groupId) {
                    navigateTo(
                      AppRoutesNavigation.Localities.localityServiceGroup(
                        (localityFrequencyDeviation.locality?.customerId ??
                          deviation?.locality?.customerId ??
                          customerId) as EntityId,
                        (localityFrequencyDeviation.locality?.id ?? deviation?.locality?.id ?? localityId) as EntityId,
                        localityFrequencyDeviation?.groupId as string,
                      ),
                    )
                  } else
                    navigateTo(
                      AppRoutesNavigation.Localities.localityService(
                        (localityFrequencyDeviation.locality?.customerId ??
                          deviation?.locality?.customerId ??
                          customerId) as EntityId,
                        (localityFrequencyDeviation.locality?.id ?? deviation?.locality?.id ?? localityId) as EntityId,
                        localityFrequencyDeviation.scheduledServiceId as EntityId,
                      ),
                    )
                }
                pop && pop()
              }}
              text={localityFrequencyDeviation?.title}
              maxLength={12}
              color={
                isClosedOrPending
                  ? getThemeColor(theme, MUIThemeColors.secondaryDark)
                  : getThemeColor(theme, MUIThemeColors.black)
              }
              fontWeight={400}
            />
          ))}
        </>
      ) : deviation?.scheduledTask?.title ? (
        <>
          <Padding.pr4>{RowMaintenanceTypeImageBox({ type: MaintenanceType.Task })}</Padding.pr4>
          <DynamicTextTypography
            onClick={e => {
              if (disabled) return
              e.stopPropagation()
              e.preventDefault()
              if (
                navigateTo &&
                (deviation?.scheduledTask?.tasks?.locality?.customerId ||
                  deviation?.locality?.customerId ||
                  customerId) &&
                (deviation?.scheduledTask?.tasks?.locality?.id || deviation?.locality?.id || localityId)
              ) {
                navigateTo(
                  AppRoutesNavigation.Localities.workOrder(
                    (deviation?.scheduledTask?.tasks?.locality?.customerId ??
                      deviation?.locality?.customerId ??
                      customerId) as EntityId,
                    (deviation?.scheduledTask?.tasks?.locality?.id ??
                      deviation?.locality?.id ??
                      localityId) as EntityId,
                    deviation?.scheduledTask?.id as EntityId,
                  ),
                )
              }
              pop && pop()
            }}
            text={deviation?.scheduledTask?.title}
            maxLength={12}
            color={
              isClosedOrPending
                ? getThemeColor(theme, MUIThemeColors.secondaryDark)
                : getThemeColor(theme, MUIThemeColors.black)
            }
            fontWeight={400}
          />
        </>
      ) : (
        '-'
      )
    },
  },
  {
    field: 'dueDate',
    headerName: t('dueDate', { ns: 'common' }),
    flex: 1,
    renderCell: ({ row }) => {
      const isClosedOrPending = row.status || (row as IDeviation).deviationStatus === DeviationStatus.InApproval
      return (
        <DynamicTextTypography
          text={getFormattedDate(new Date(row.dueDate))}
          maxLength={20}
          color={
            isClosedOrPending
              ? getThemeColor(theme, MUIThemeColors.secondaryDark)
              : getThemeColor(theme, MUIThemeColors.black)
          }
          fontWeight={400}
        />
      )
    },
  },
]
